import { useState, useEffect } from 'react';

export const motion = {
  duration: {
    fast: '150ms',
    normal: '250ms',
    slow: '400ms',
  },
  easing: {
    default: 'cubic-bezier(0.4, 0, 0.2, 1)',
    spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    decelerate: 'cubic-bezier(0, 0, 0.2, 1)',
  },
  stagger: 50,
} as const;

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return reduced;
}

let injected = false;

export function injectMotionKeyframes(): void {
  if (injected || typeof document === 'undefined') return;
  injected = true;

  const css = `
@keyframes cp-slide-up {
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
}
@keyframes cp-scale-in {
  from { transform: scale(0.92); opacity: 0; }
  to   { transform: scale(1); opacity: 1; }
}
@keyframes cp-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes cp-slide-in-bottom {
  from { transform: translateY(16px); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
}
@keyframes cp-shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-6px); }
  40% { transform: translateX(5px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(3px); }
}
@keyframes cp-pulse-badge {
  0% { transform: scale(1); }
  50% { transform: scale(1.12); }
  100% { transform: scale(1); }
}
@keyframes cp-expand {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes cp-sparkline-draw {
  from { stroke-dashoffset: var(--cp-path-len); }
  to   { stroke-dashoffset: 0; }
}
@keyframes cp-progress-fill {
  from { width: 0%; }
}
@keyframes cp-tab-indicator {
  from { transform: scaleX(0.7); opacity: 0.5; }
  to   { transform: scaleX(1); opacity: 1; }
}
@keyframes cp-person-in {
  from { opacity: 0; transform: translateX(8px) scale(0.85); }
  to   { opacity: 1; transform: translateX(0) scale(1); }
}
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}`;

  const style = document.createElement('style');
  style.setAttribute('data-cp-motion', '');
  style.textContent = css;
  document.head.appendChild(style);
}
